import { artRectIn, LOOKS, quadToSquare, SHADOW_STRENGTH, type LookParams, type MockupJob, type MockupTemplate, type PrintArea, type Rgb } from '@eip/engine';
import type { PreviewMaps } from '../worker/protocol';

/**
 * The key one scene's cover is held under, everywhere: the scene it was cut from and the size it was
 * cut to. Both halves are needed — the compositor reads a cover pixel for pixel, so a cover cut for
 * one preview size is the wrong image for another, and a cache keyed by scene alone would hand it
 * over anyway. The scene id is a fresh UUID per import (`addScene`), so it names the PIXELS as
 * exactly as a hex names a colour and nothing has to be invalidated when one is re-imported.
 *
 * `|` and `x` rather than `:` throughout, so `dropSceneCovers`'s prefix scan cannot mistake part of
 * a size for part of an id.
 */
export function sceneCoverKey(sceneId: string, width: number, height: number): string {
  return `${sceneId}|${width}x${height}`;
}

/**
 * The backdrop as the base pass wants it: a kind the shader switches on and two colours in 0..1
 * (the CPU's `backdropAt` works in 0..255; the division happens here, once, rather than per pixel).
 *
 * `kind` 0 is "the photo's own", which is every case the GPU must not draw: no backdrop on the job,
 * a non-separable blank (no shadow map — the same test `renderMockup` makes), or a scene whose cover
 * has not been uploaded yet. The shader then leaves the photo's own pixels exactly as they are.
 */
export interface GlBackdrop {
  /** 0 none, 1 colour (`a`), 2 gradient (`a` = top, `b` = bottom), 3 scene (`sceneKey`). */
  kind: 0 | 1 | 2 | 3;
  a: [number, number, number];
  b: [number, number, number];
  /** The `sceneCoverKey` the renderer looks the cover texture up by, for `kind` 3 only. */
  sceneKey: string | null;
}

const NO_BACKDROP: GlBackdrop = { kind: 0, a: [0, 0, 0], b: [0, 0, 0], sceneKey: null };
const unit = (rgb: Rgb): [number, number, number] => [rgb[0] / 255, rgb[1] / 255, rgb[2] / 255];

/**
 * A job's backdrop for the GPU, plus the shadow strength the pass should apply — which is
 * `SHADOW_STRENGTH` exactly when there is a backdrop to draw and 0 when there is not, so the two can
 * never disagree about whether the drop shadow is on.
 *
 * `covers` is what the CALLER has uploaded: a scene whose cover is still in flight draws no backdrop
 * at all rather than a garment floating on the photo's own, because half the swap is worse than none
 * — the CPU render underneath is showing the right picture until the cover lands.
 */
function backdropFor(job: MockupJob, maps: PreviewMaps, covers: ReadonlySet<string> | undefined): { backdrop: GlBackdrop; shadowStrength: number } {
  const none = { backdrop: NO_BACKDROP, shadowStrength: 0 };
  const b = job.backdrop;
  // No shadow map means a blank whose garment could not be told from its backdrop, and
  // `renderMockup` ignores the backdrop for exactly that reason (`composite.ts`: `input.backdrop &&
  // maps.shadow`). Mirrored here rather than assumed, so the GPU cannot paint one the CPU will not.
  if (!b || !maps.shadow) return none;
  if (b.kind === 'color') return { backdrop: { kind: 1, a: unit(b.rgb), b: [0, 0, 0], sceneKey: null }, shadowStrength: SHADOW_STRENGTH };
  if (b.kind === 'gradient') return { backdrop: { kind: 2, a: unit(b.top), b: unit(b.bottom), sceneKey: null }, shadowStrength: SHADOW_STRENGTH };
  const key = sceneCoverKey(b.sceneId, maps.width, maps.height);
  if (!covers?.has(key)) return none;
  return { backdrop: { kind: 3, a: [0, 0, 0], b: [0, 0, 0], sceneKey: key }, shadowStrength: SHADOW_STRENGTH };
}

/**
 * One print pass's worth of uniforms — everything `renderMockup`'s `draw` closure hoists out of its
 * per-pixel loop, in the units the shader wants.
 *
 * `hInv` is `quadToSquare(area.quad)` (row-major) TRANSPOSED into the column-major order
 * `uniformMatrix3fv(loc, false, …)` uploads, so `uHinv * vec3(q, 1.0)` in GLSL evaluates the same
 * `(h0·x + h1·y + h2, h3·x + h4·y + h5, h6·x + h7·y + h8)` the CPU inlines.
 */
export interface GlPrint {
  /** 3×3 column-major: preview px → the area quad's unit square (u, v). */
  hInv: Float32Array;
  areaIn: [number, number];
  /** x0, y0, w, h — inches in the area's own space (`artRectIn`). */
  rect: [number, number, number, number];
  cos: number;
  sin: number;
  /** The key the renderer looks the art texture up by (`setArt`'s id). */
  artId: string;
  artSize: [number, number];
  /** Always true on a print `glJobFor` kept (a disabled one never reaches the list); the renderer
   *  still honours it, so a caller can mute one print for a frame without rebuilding the job. */
  enabled: boolean;
}

/** A frame's worth of uniforms: the garment pass plus one entry per print, in draw order. */
export interface GlJob {
  /** The requested garment colour, or null when the job asks for none OR the blank cannot be
   *  recoloured at all — `maps.recolorable` is folded in here so the renderer has one thing to test. */
  garment: Rgb | null;
  /** The blank's own colour, which the recolour scales the photo's shading against. */
  garmentColor: Rgb;
  look: LookParams;
  /** The job's (or template's) texture amount, BEFORE the look's own factor — the shader multiplies. */
  texture: number;
  /** `wrinkle × previewWidth`: the shader's gx/gy are SNORM (already ÷ 127), so the CPU's
   *  `wrinkle · W / 127` loses its divisor here. */
  dispPx: number;
  lmid: number;
  /** What the base pass paints where the garment is not, before any print lands on top of it —
   *  exactly where `renderMockup` calls `applyBackdrop`, over the recoloured and rounded base. */
  backdrop: GlBackdrop;
  /** The engine's `SHADOW_STRENGTH` (0.35) while a backdrop is drawn, 0 otherwise. */
  shadowStrength: number;
  prints: GlPrint[];
}

const printFor = (area: PrintArea, rectIn: { x0: number; y0: number; w: number; h: number }, deg: number, artId: string, art: { width: number; height: number }): GlPrint => {
  const h = quadToSquare(area.quad);
  const th = (deg * Math.PI) / 180;
  return {
    hInv: new Float32Array([h[0], h[3], h[6], h[1], h[4], h[7], h[2], h[5], h[8]]),
    areaIn: [area.widthIn, area.heightIn],
    rect: [rectIn.x0, rectIn.y0, rectIn.w, rectIn.h],
    cos: Math.cos(th), sin: Math.sin(th),
    artId, artSize: [art.width, art.height], enabled: true,
  };
};

/**
 * The GPU form of a `MockupJob` against one set of preview maps — the pure half of the WebGL
 * preview, so that everything the shader is given can be tested in node.
 *
 * Mirrors `renderMockup`'s bookkeeping exactly: the primary print first (`maps.areas[0]`, the job's
 * own art), then each ENABLED extra print whose area these maps carry and whose art the caller
 * knows, in the job's order — overlapping areas layer in the order they are drawn, as they do on the
 * CPU. Unlike the CPU it reports nothing: a print whose art is not open (or whose area this blank
 * does not have) is simply left out of the frame, and the viewer's skipped-area list still comes
 * from the CPU render. With no primary art there is nothing to draw at all.
 *
 * `template` is here only for the two shading defaults a job may leave null (`wrinkle`, `texture`),
 * which is exactly where `renderMockup` reads them from; the AREAS come from the maps, because those
 * quads are in preview pixels.
 *
 * `covers` is the set of scene-cover keys (`sceneCoverKey`) the caller has uploaded to the renderer,
 * and it plays the part `arts` plays for the prints: a scene backdrop whose cover is not there yet
 * is left out of the frame (`backdrop.kind` 0) rather than drawn from a stale or missing texture.
 */
export function glJobFor(
  maps: PreviewMaps,
  template: MockupTemplate,
  job: MockupJob,
  arts: Record<string, { width: number; height: number }>,
  selfId: string,
  covers?: ReadonlySet<string>,
): GlJob {
  const base: Omit<GlJob, 'prints'> = {
    ...backdropFor(job, maps, covers),
    // A blank whose mask could not be separated from the backdrop would have its model and backdrop
    // repainted too, so `recolorable: false` refuses the recolour outright — as the UI does.
    garment: job.garment && maps.recolorable ? job.garment : null,
    garmentColor: maps.garmentColor,
    look: LOOKS[job.look],
    texture: job.texture ?? template.shading.texture,
    dispPx: (job.wrinkle ?? template.shading.wrinkle) * maps.width,
    lmid: maps.lmid,
  };
  const primaryArt = arts[selfId];
  if (!primaryArt) return { ...base, prints: [] };
  const area0 = maps.areas[0];
  const prints: GlPrint[] = [printFor(area0, artRectIn(area0, job.placement, primaryArt), job.rotationDeg ?? 0, selfId, primaryArt)];
  for (const p of job.extraPrints ?? []) {
    if (!p || !p.enabled) continue;
    const area = maps.areas.find((a) => a.id === p.areaId);
    if (!area) continue;
    // `docId: null` means "this job's own art" — the same fallback `renderMockup` spells `'self'`.
    const artId = p.docId ?? selfId;
    const art = arts[artId];
    if (!art) continue;
    prints.push(printFor(area, artRectIn(area, p.placement, art), p.rotationDeg ?? 0, artId, art));
  }
  return { ...base, prints };
}
